// WEEKLY-3 (2026-07-27) — Detalhe de uma reunião semanal.
// Briefing (gerado pelo sistema) + Ata (Tiptap) + Transcrição (upload S3
// presigned) + Encaminhamentos (painel próprio). Navegação por estado interno
// (a página-mãe OperacionalReunioes controla qual id está aberto).

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  App as AntApp, Button, Collapse, DatePicker, Empty, Flex, Input, Modal,
  Space, Spin, Tag, Typography, Upload,
} from 'antd';
import {
  ArrowLeftOutlined, CheckOutlined, CloseOutlined, DeleteOutlined,
  EditOutlined, EyeOutlined, RobotOutlined, SafetyCertificateOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import RichTextEditor from '../RichTextEditor';
import { usePerms } from '../../hooks/usePerms';
import EncaminhamentosPanel from './EncaminhamentosPanel';
import { api, erroMsg, fmtSemana, isoDia, nomeBase } from './utils';
import {
  Reuniao, Encaminhamento, STATUS_REUNIAO, PERM_GERENCIAR, PERM_CONSOLIDAR,
} from './types';

const { Text, Title } = Typography;

interface Props {
  id: string;
  onVoltar: () => void;
}

export default function ReuniaoDetalhe({ id, onVoltar }: Props) {
  const { message, modal } = AntApp.useApp();
  const { pode } = usePerms();

  const [reuniao, setReuniao] = useState<Reuniao | null>(null);
  const [encams, setEncams] = useState<Encaminhamento[]>([]);
  const [loading, setLoading] = useState(true);

  // Ata (editor local + dirty). ataDirtyRef espelha o dirty pro carregar()
  // não sobrescrever edições não salvas quando outro campo é salvo.
  const [ataHtml, setAtaHtml] = useState('');
  const [ataDirty, setAtaDirty] = useState(false);
  const ataDirtyRef = useRef(false);
  const [salvandoAta, setSalvandoAta] = useState(false);

  // Título inline
  const [editandoTitulo, setEditandoTitulo] = useState(false);
  const [tituloEdit, setTituloEdit] = useState('');
  const [salvandoTitulo, setSalvandoTitulo] = useState(false);

  // Ações do header
  const [gerando, setGerando] = useState(false);
  const [marcando, setMarcando] = useState(false);
  const [consolidando, setConsolidando] = useState(false);

  // Transcrição
  const [uploadando, setUploadando] = useState(false);
  const [carregandoTexto, setCarregandoTexto] = useState(false);
  const [modalTexto, setModalTexto] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api('op_reuniao_obter', { id });
      setReuniao(d.reuniao || null);
      setEncams(Array.isArray(d.encaminhamentos) ? d.encaminhamentos : []);
      if (!ataDirtyRef.current) {
        setAtaHtml(d.reuniao?.ata_html || '');
        setAtaDirty(false);
      }
    } catch (e: any) {
      message.error('Falha ao carregar reunião: ' + erroMsg(e));
    } finally { setLoading(false); }
  }, [id, message]);
  useEffect(() => { carregar(); }, [carregar]);

  const consolidada = reuniao?.status === 'consolidada';
  const podeGerenciar = pode(PERM_GERENCIAR) && !consolidada;

  async function salvarCampos(campos: Record<string, any>) {
    try {
      await api('op_reuniao_atualizar', { id, ...campos });
      await carregar();
    } catch (e: any) {
      message.error('Falha ao salvar: ' + erroMsg(e));
    }
  }

  async function salvarTitulo() {
    const t = tituloEdit.trim();
    if (!t || !reuniao || t === reuniao.titulo) { setEditandoTitulo(false); return; }
    setSalvandoTitulo(true);
    try {
      await api('op_reuniao_atualizar', { id, titulo: t });
      setEditandoTitulo(false);
      await carregar();
    } catch (e: any) {
      message.error('Falha: ' + erroMsg(e));
    } finally { setSalvandoTitulo(false); }
  }

  async function salvarAta() {
    setSalvandoAta(true);
    try {
      await api('op_reuniao_atualizar', { id, ata_html: ataHtml });
      ataDirtyRef.current = false;
      setAtaDirty(false);
      message.success('Ata salva.');
    } catch (e: any) {
      message.error('Falha ao salvar a ata: ' + erroMsg(e));
    } finally { setSalvandoAta(false); }
  }

  async function gerarBriefing() {
    setGerando(true);
    try {
      await api('op_reuniao_gerar_briefing', { id }, 120000);
      message.success('Briefing gerado.');
      await carregar();
    } catch (e: any) {
      message.error('Falha ao gerar briefing: ' + erroMsg(e));
    } finally { setGerando(false); }
  }

  async function marcarRealizada() {
    setMarcando(true);
    try {
      await api('op_reuniao_atualizar', { id, status: 'realizada' });
      message.success('Reunião marcada como realizada.');
      await carregar();
    } catch (e: any) {
      message.error('Falha: ' + erroMsg(e));
    } finally { setMarcando(false); }
  }

  function confirmarConsolidar() {
    const aceitos = encams.filter(e => e.status === 'aceito').length;
    modal.confirm({
      title: 'Consolidar esta reunião?',
      icon: <SafetyCertificateOutlined style={{ color: '#0a7f4a' }} />,
      content: (
        <span>
          A reunião fica <strong>fechada pra edição</strong> e os encaminhamentos
          <strong> aceitos</strong> pendentes ({aceitos}) são convertidos em ações
          nas atividades. Encaminhamentos propostos que não foram aceitos ficam
          como estão.
        </span>
      ),
      okText: 'Consolidar',
      onOk: async () => {
        setConsolidando(true);
        try {
          await api('op_reuniao_consolidar', { id }, 120000);
          message.success('Reunião consolidada.');
          await carregar();
        } catch (e: any) {
          message.error('Falha ao consolidar: ' + erroMsg(e));
        } finally { setConsolidando(false); }
      },
    });
  }

  function confirmarExcluir() {
    modal.confirm({
      title: 'Excluir esta reunião?',
      content: 'Encaminhamentos vinculados também são removidos. Só admin ou o criador podem excluir (e reunião consolidada não pode).',
      okText: 'Excluir', okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await api('op_reuniao_excluir', { id });
          message.success('Reunião excluída.');
          onVoltar();
        } catch (e: any) {
          message.error('Falha ao excluir: ' + erroMsg(e));
        }
      },
    });
  }

  // ─── Transcrição: presigned PUT → confirmar ─────────────────────────
  async function subirTranscricao(file: File) {
    setUploadando(true);
    try {
      const d1 = await api('op_reuniao_transcricao_upload_url', { id, filename: file.name });
      await axios.put(d1.url, file, {
        headers: { 'Content-Type': file.type || 'text/plain' },
      });
      await api('op_reuniao_transcricao_confirmar', { id, s3_key: d1.s3_key });
      message.success('Transcrição anexada.');
      await carregar();
    } catch (e: any) {
      message.error('Falha no upload: ' + erroMsg(e));
    } finally { setUploadando(false); }
  }

  async function verTranscricao() {
    setCarregandoTexto(true);
    try {
      const d = await api('op_reuniao_transcricao_texto', { id });
      setModalTexto(d?.texto ?? '');
    } catch (e: any) {
      message.error('Falha ao ler transcrição: ' + erroMsg(e));
    } finally { setCarregandoTexto(false); }
  }

  if (loading && !reuniao) {
    return <div style={{ padding: 60, textAlign: 'center' }}><Spin size="large" /></div>;
  }
  if (!reuniao) {
    return (
      <div style={{ padding: 30 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onVoltar}>Voltar</Button>
        <Empty style={{ marginTop: 30 }} description="Reunião não encontrada." />
      </div>
    );
  }

  const st = STATUS_REUNIAO[reuniao.status] || STATUS_REUNIAO.agendada;

  return (
    <div>
      {/* ─── Header ───────────────────────────────────────────── */}
      <Flex align="center" gap={10} wrap="wrap" style={{ marginBottom: 6 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onVoltar} title="Voltar pra lista" />
        {editandoTitulo ? (
          <Space.Compact>
            <Input autoFocus value={tituloEdit} disabled={salvandoTitulo}
              onChange={e => setTituloEdit(e.target.value)}
              onPressEnter={salvarTitulo} style={{ width: 340 }} />
            <Button type="primary" loading={salvandoTitulo} onClick={salvarTitulo}>
              <CheckOutlined />
            </Button>
            <Button onClick={() => setEditandoTitulo(false)} icon={<CloseOutlined />} />
          </Space.Compact>
        ) : (
          <Title level={4} style={{ margin: 0 }}>
            {reuniao.titulo}
            {podeGerenciar && (
              <Button type="text" size="small" icon={<EditOutlined />}
                style={{ marginLeft: 4, color: '#0930D0' }}
                onClick={() => { setTituloEdit(reuniao.titulo); setEditandoTitulo(true); }} />
            )}
          </Title>
        )}
        <Tag color={st.cor}>{st.label}</Tag>
        {reuniao.origem === 'notion_import' && <Tag>📥 Notion</Tag>}
      </Flex>

      <Flex align="center" gap={10} wrap="wrap" style={{ marginBottom: 16 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Semana <strong>{fmtSemana(reuniao.semana_ref)}</strong> · Reunião:
        </Text>
        {podeGerenciar ? (
          <DatePicker size="small" allowClear={false} format="DD/MM/YYYY"
            value={reuniao.data_reuniao ? dayjs(isoDia(reuniao.data_reuniao)) : null}
            onChange={(d) => { if (d) salvarCampos({ data_reuniao: d.format('YYYY-MM-DD') }); }} />
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {reuniao.data_reuniao ? dayjs(isoDia(reuniao.data_reuniao)).format('DD/MM/YYYY') : '—'}
          </Text>
        )}
        {(reuniao.participantes || []).length > 0 && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            👥 {(reuniao.participantes || []).join(', ')}
          </Text>
        )}
        <span style={{ flex: 1 }} />
        <Space wrap>
          {podeGerenciar && (
            <Button icon={<RobotOutlined />} loading={gerando} onClick={gerarBriefing}
              style={{ background: '#7C3AED', borderColor: '#7C3AED', color: '#fff' }}>
              {reuniao.briefing_html ? 'Regenerar briefing' : 'Gerar briefing'}
            </Button>
          )}
          {podeGerenciar && (reuniao.status === 'agendada' || reuniao.status === 'briefing_gerado') && (
            <Button icon={<CheckOutlined />} loading={marcando} onClick={marcarRealizada}>
              Marcar realizada
            </Button>
          )}
          {pode(PERM_CONSOLIDAR) && !consolidada && (
            <Button type="primary" icon={<SafetyCertificateOutlined />}
              loading={consolidando} onClick={confirmarConsolidar}>
              Consolidar
            </Button>
          )}
          {pode(PERM_GERENCIAR) && !consolidada && (
            <Button danger type="text" icon={<DeleteOutlined />}
              onClick={confirmarExcluir} title="Excluir reunião (admin ou criador)" />
          )}
        </Space>
      </Flex>

      {/* ─── Seções colapsáveis ───────────────────────────────── */}
      <Collapse defaultActiveKey={['briefing', 'ata', 'transcricao', 'encaminhamentos']}
        items={[
          {
            key: 'briefing',
            label: <Text strong>📋 Briefing</Text>,
            children: reuniao.briefing_html ? (
              <div>
                {/* Backend escapa o conteúdo vindo do banco antes de montar o HTML */}
                <div className="weekly-briefing"
                  dangerouslySetInnerHTML={{ __html: reuniao.briefing_html }} />
                {reuniao.pauta_html && (
                  <details style={{ marginTop: 10 }}>
                    <summary style={{ fontSize: 12, color: '#020D7F', fontWeight: 600, cursor: 'pointer' }}>
                      📌 Pauta
                    </summary>
                    <div className="weekly-briefing" style={{ paddingTop: 6 }}
                      dangerouslySetInnerHTML={{ __html: reuniao.pauta_html }} />
                  </details>
                )}
              </div>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Briefing ainda não gerado — dados da semana + síntese IA.">
                {podeGerenciar && (
                  <Button icon={<RobotOutlined />} loading={gerando} onClick={gerarBriefing}
                    style={{ background: '#7C3AED', borderColor: '#7C3AED', color: '#fff' }}>
                    Gerar briefing agora
                  </Button>
                )}
              </Empty>
            ),
          },
          {
            key: 'ata',
            label: (
              <Space size={8}>
                <Text strong>📝 Ata</Text>
                {ataDirty && <Tag color="orange" style={{ fontSize: 10 }}>alterações não salvas</Tag>}
              </Space>
            ),
            children: podeGerenciar ? (
              <div>
                <RichTextEditor value={ataHtml}
                  onChange={(html) => {
                    setAtaHtml(html);
                    ataDirtyRef.current = true;
                    setAtaDirty(true);
                  }}
                  height={420} />
                <Flex justify="space-between" align="center" style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {ataDirty ? '● Há alterações não salvas' : 'Tudo salvo'}
                  </Text>
                  <Button type="primary" loading={salvandoAta} disabled={!ataDirty}
                    onClick={salvarAta}>
                    Salvar ata
                  </Button>
                </Flex>
              </div>
            ) : (
              reuniao.ata_html
                ? <div className="weekly-briefing" dangerouslySetInnerHTML={{ __html: reuniao.ata_html }} />
                : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sem ata registrada." />
            ),
          },
          {
            key: 'transcricao',
            label: <Text strong>🎙️ Transcrição</Text>,
            children: (
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {reuniao.transcricao_s3_key ? (
                  <Text style={{ fontSize: 12 }}>
                    Arquivo anexado: <code>{nomeBase(reuniao.transcricao_s3_key)}</code>
                  </Text>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Nenhuma transcrição anexada (.vtt ou .txt exportado do Teams).
                    A IA usa ela na extração de encaminhamentos.
                  </Text>
                )}
                <Space wrap>
                  {podeGerenciar && (
                    <Upload accept=".vtt,.txt" showUploadList={false}
                      beforeUpload={(f) => { subirTranscricao(f as File); return false; }}>
                      <Button icon={<UploadOutlined />} loading={uploadando}>
                        {reuniao.transcricao_s3_key ? 'Substituir transcrição' : 'Enviar transcrição'}
                      </Button>
                    </Upload>
                  )}
                  {reuniao.transcricao_s3_key && (
                    <Button icon={<EyeOutlined />} loading={carregandoTexto} onClick={verTranscricao}>
                      Ver transcrição
                    </Button>
                  )}
                </Space>
              </Space>
            ),
          },
          {
            key: 'encaminhamentos',
            label: (
              <Space size={8}>
                <Text strong>✅ Encaminhamentos</Text>
                <Tag style={{ fontSize: 10 }}>{encams.length}</Tag>
              </Space>
            ),
            children: (
              <EncaminhamentosPanel reuniaoId={id} statusReuniao={reuniao.status}
                encaminhamentos={encams} onRecarregar={carregar} />
            ),
          },
        ]} />

      {/* Modal da transcrição em texto */}
      <Modal open={modalTexto !== null} onCancel={() => setModalTexto(null)}
        title="🎙️ Transcrição" width={800}
        footer={<Button onClick={() => setModalTexto(null)}>Fechar</Button>}>
        <pre style={{
          maxHeight: '60vh', overflow: 'auto', whiteSpace: 'pre-wrap',
          fontSize: 12, background: '#fafafa', padding: 12, borderRadius: 6,
          margin: 0,
        }}>{modalTexto || '(vazio)'}</pre>
      </Modal>

      {/* Estilos do HTML renderizado (briefing/pauta/ata em modo leitura) */}
      <style>{`
        .weekly-briefing { font-size: 13px; line-height: 1.55; color: #222; }
        .weekly-briefing h1 { font-size: 20px; color: #020D7F; margin: 14px 0 8px; }
        .weekly-briefing h2 { font-size: 16px; color: #020D7F; margin: 12px 0 6px; }
        .weekly-briefing h3 { font-size: 13px; color: #020D7F; margin: 10px 0 4px; }
        .weekly-briefing p { margin: 4px 0; }
        .weekly-briefing ul, .weekly-briefing ol { padding-left: 22px; margin: 4px 0; }
        .weekly-briefing table { border-collapse: collapse; width: 100%; margin: 8px 0; }
        .weekly-briefing th, .weekly-briefing td { border: 0.5px solid #ddd; padding: 5px 8px; font-size: 12px; vertical-align: top; }
        .weekly-briefing th { background: #020D7F; color: #fff; }
        .weekly-briefing blockquote { border-left: 3px solid #ddd; padding-left: 12px; color: #666; }
        .weekly-briefing img { max-width: 100%; height: auto; }
      `}</style>
    </div>
  );
}
